import { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "stabilo_coachmark_seen";
const FUCHSIA = "#FF2EC8";
const GOLD = "#F5EFE3";

interface Props {
  /** Delay in ms before the coachmark appears on first load. */
  delay?: number;
}

/**
 * One-time centered coachmark teaching users the Stabilo gesture.
 * Animates a simulated text selection (fuchsia sweep over the phrase
 * "Surlignez n'importe quel passage") followed by the Stabilo toolbar.
 * Closing the card dismisses everything — the demo toolbar included.
 */
export function StabiloCoachmark({ delay = 800 }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  if (!visible) return null;

  const actions = ["Approfondir", "Expliquer", "Vérifier", "Non pertinent"];

  return (
    <>
      <div
        className="stabilo-coach__backdrop"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stabilo-coach-title"
        data-testid="stabilo-coachmark"
        className="stabilo-coach"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="stabilo-coach__close"
        >
          <X size={16} />
        </button>

        <div className="stabilo-coach__kicker">
          <span style={{ color: GOLD, marginRight: 8 }}>§</span>
          Astuce du Conseil
        </div>

        <h3 id="stabilo-coach-title" className="stabilo-coach__title">
          Creusez avec le Conseil
        </h3>

        <p className="stabilo-coach__text">
          Surlignez n'importe quel passage du verdict pour interroger le
          Conseil sur ce point précis.
        </p>

        <div className="stabilo-coach__demo" aria-hidden="true">
          <div className="stabilo-coach__demo-line">
            <span className="stabilo-coach__demo-text">
              <span className="stabilo-coach__demo-hl">
                <span className="stabilo-coach__demo-sweep" />
                <span className="stabilo-coach__demo-hl-text">
                  Surlignez n'importe quel passage
                </span>
              </span>{" "}
              pour poser une question au Conseil.
            </span>

            <span className="stabilo-coach__demo-toolbar">
              {actions.map((a) => (
                <span key={a} className="stabilo-coach__demo-chip">
                  {a}
                </span>
              ))}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="stabilo-coach__cta"
        >
          Compris
        </button>
      </div>

      <style>{`
        .stabilo-coach__backdrop {
          position: fixed;
          inset: 0;
          background: rgba(10, 8, 6, 0.62);
          backdrop-filter: blur(2px);
          z-index: 59;
          animation: stabilo-fade-in 220ms ease-out both;
        }
        .stabilo-coach {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 60;
          width: min(440px, calc(100vw - 32px));
          background: #0F0F0F;
          color: #F5F1E8;
          border: 0.5px solid ${GOLD};
          padding: 28px 28px 24px;
          box-shadow: 0 30px 80px -20px rgba(0,0,0,0.65);
          font-family: var(--qv3-font-serif, "Fraunces", Georgia, serif);
          animation: stabilo-coach-in 360ms ease-out both;
        }
        @keyframes stabilo-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes stabilo-coach-in {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 12px)); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
        .stabilo-coach__close {
          position: absolute; top: 12px; right: 12px;
          background: transparent; border: 0; cursor: pointer;
          color: #B5AE9E; padding: 4px; line-height: 0;
          transition: color .15s;
        }
        .stabilo-coach__close:hover { color: ${GOLD}; }
        .stabilo-coach__kicker {
          font-family: var(--qv3-font-mono, "JetBrains Mono", ui-monospace, monospace);
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #B5AE9E;
          margin-bottom: 12px;
        }
        .stabilo-coach__title {
          font-family: var(--qv3-font-serif, "Playfair Display", "Fraunces", Georgia, serif);
          font-weight: 400;
          font-size: 24px;
          line-height: 1.2;
          letter-spacing: -0.01em;
          margin: 0 0 10px;
          color: #FFFFFF;
        }
        .stabilo-coach__text {
          font-size: 14px;
          line-height: 1.55;
          color: #D8D2C2;
          margin: 0 0 18px;
        }
        .stabilo-coach__demo {
          background: #1A1A1A;
          border: 0.5px solid rgba(255,23,68,0.25);
          padding: 22px 18px 72px;
          margin: 0 0 20px;
          position: relative;
          overflow: hidden;
        }
        .stabilo-coach__demo-line {
          position: relative;
          font-family: var(--qv3-font-serif, "Playfair Display", "Fraunces", Georgia, serif);
          font-style: italic;
          font-size: 14px;
          line-height: 1.65;
          color: #ECE6D6;
          text-align: center;
        }
        .stabilo-coach__demo-text {
          position: relative;
          display: inline-block;
          animation: stabilo-text-fade 3.6s ease-in-out infinite;
        }
        @keyframes stabilo-text-fade {
          0%, 45%   { opacity: 1; }
          52%, 88%  { opacity: 0; }
          96%, 100% { opacity: 1; }
        }
        .stabilo-coach__demo-hl {
          position: relative;
          display: inline-block;
          padding: 0 2px;
        }
        .stabilo-coach__demo-hl-text {
          position: relative;
          z-index: 1;
          transition: color .2s;
          animation: stabilo-text-color 3.6s ease-in-out infinite;
        }
        .stabilo-coach__demo-sweep {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 0;
          background: ${FUCHSIA};
          z-index: 0;
          animation: stabilo-sweep 3.6s ease-in-out infinite;
        }
        .stabilo-coach__demo-toolbar {
          position: absolute;
          left: 50%;
          bottom: 14px;
          transform: translateX(-50%) translateY(6px);
          opacity: 0;
          display: inline-flex;
          gap: 4px;
          background: #FBF8F1;
          border: 0.5px solid #FF1744;
          border-radius: 999px;
          padding: 4px 6px;
          box-shadow: 0 10px 24px rgba(0,0,0,0.35);
          animation: stabilo-toolbar 3.6s ease-in-out infinite;
        }
        .stabilo-coach__demo-chip {
          font-family: var(--qv3-font-mono, "DM Mono", ui-monospace, monospace);
          font-size: 9px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #1A1714;
          padding: 5px 9px;
          border-radius: 999px;
          white-space: nowrap;
        }
        @keyframes stabilo-sweep {
          0%   { width: 0; }
          15%  { width: 0; }
          45%  { width: 100%; }
          85%  { width: 100%; }
          95%  { width: 100%; opacity: 1; }
          100% { width: 0; opacity: 0; }
        }
        @keyframes stabilo-text-color {
          0%, 14%   { color: #ECE6D6; }
          45%, 85%  { color: #1A1714; }
          100%      { color: #ECE6D6; }
        }
        @keyframes stabilo-toolbar {
          0%, 45%   { opacity: 0; transform: translateX(-50%) translateY(6px); }
          55%, 85%  { opacity: 1; transform: translateX(-50%) translateY(0); }
          95%, 100% { opacity: 0; transform: translateX(-50%) translateY(6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stabilo-coach { animation: none; }
          .stabilo-coach__backdrop { animation: none; }
          .stabilo-coach__demo-sweep { animation: none; width: 100%; }
          .stabilo-coach__demo-hl-text { animation: none; color: #1A1714; }
          .stabilo-coach__demo-text { animation: none; opacity: 0; }
          .stabilo-coach__demo-toolbar { animation: none; opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .stabilo-coach__cta {
          display: block;
          width: 100%;
          background: transparent;
          border: 0.5px solid ${GOLD};
          color: ${GOLD};
          font-family: var(--qv3-font-mono, "JetBrains Mono", ui-monospace, monospace);
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          padding: 12px 16px;
          cursor: pointer;
          transition: background .15s, color .15s;
        }
        .stabilo-coach__cta:hover {
          background: ${GOLD};
          color: #0F0F0F;
        }
      `}</style>
    </>
  );
}

export default StabiloCoachmark;
